import "../../candidate-intelligence/results/styles.css";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import {
  CalibrationSteps,
  Muted,
  Shell,
  type StepItem,
  WorkflowStepper,
} from "../calibrate/calibration-steps";
import { C, DM, UI } from "../results/ui-tokens";
import { DisciplineMatrixStep } from "./discipline-step";
import ModelsNewPage from "./new";

/**
 * The create→calibrate **WorkflowShell** — one continuous journey shared by both
 * authoring entry points:
 *   - "+ Novo modelo" (model-management screen) → `new_model`: defines a
 *     **cross-only baseline** matrix, then calibrates the whole model.
 *   - "Adicionar disciplina" (model-edit screen) → `new_discipline`: defines one
 *     discipline's vertical matrix, then calibrates that discipline.
 *
 * Both run the same four steps — Matriz → Perfis → Validação → Aprendizado —
 * with steps 2–4 (calibration) as the **opt-out default**: after the matrix is
 * saved + bootstrapped we create a calibration session and flow straight in;
 * the user can "Calibrar depois" at any point. Step 1 differs only in scope, so
 * it delegates to the model wizard (workflow mode) or the discipline step; the
 * calibration core is the shared `CalibrationSteps`.
 */
export default function LevelAssessmentCreateWorkflow() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [isNewDiscipline, discParams] = useRoute(
    "/labs/level-assessment/models/:modelId/disciplines/new",
  );
  const kind: "new_model" | "new_discipline" = isNewDiscipline ? "new_discipline" : "new_model";
  const modelId = isNewDiscipline ? (discParams?.modelId ?? "") : null;

  const [phase, setPhase] = useState<"matrix" | "calibrate">("matrix");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [creatingSession, setCreatingSession] = useState(false);

  /** Where to go when the journey is left or finished — opting out of
   *  calibration (or a session-create failure) returns the user to the model-
   *  management screen, for BOTH a new model and a new discipline. */
  const exitHref = "/labs/level-assessment/models";

  /** Step 1 done (matrix saved + bootstrapped): open a calibration session and
   *  advance into steps 2–4. If the session can't be created the model/discipline
   *  is already persisted, so we surface the error and route somewhere useful
   *  rather than trapping the user. */
  async function startSession(scope: { modelId: string; disciplineId: string | null }) {
    setCreatingSession(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentCalibrationSessionCreate({
        modelId: scope.modelId,
        disciplineId: scope.disciplineId,
        kind,
      })) as { session: { id: string } };
      setSessionId(res.session.id);
      setPhase("calibrate");
    } catch (e) {
      toast({
        title: "Salvo, mas não consegui iniciar a calibração",
        description: e instanceof Error ? e.message : "Calibre depois pela lista de modelos.",
        variant: "destructive",
      });
      navigate(exitHref);
    } finally {
      setCreatingSession(false);
    }
  }

  if (creatingSession) {
    return (
      <Shell>
        <Muted>Preparando a calibração…</Muted>
      </Shell>
    );
  }

  if (phase === "calibrate" && sessionId) {
    return (
      <Shell>
        <header>
          <p
            style={{
              ...UI,
              margin: 0,
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              color: C.ink50,
            }}
          >
            {kind === "new_discipline" ? "Nova disciplina" : "Novo modelo"} · Calibração
          </p>
          <h1 style={{ ...DM, margin: "2px 0 0", fontSize: 22, fontWeight: 800, color: C.ink }}>
            Ensine {kind === "new_discipline" ? "a nova disciplina" : "o novo modelo"} com perfis de
            referência
          </h1>
          <p style={{ ...UI, margin: "6px 0 0", fontSize: 13.5, color: C.ink70, maxWidth: 620 }}>
            A matriz já está pronta. Agora adicione profissionais cujo nível você conhece: o modelo
            estima, você confirma ou corrige cada competência, e ele aprende com as suas validações.
            Você pode pular e calibrar depois.
          </p>
        </header>

        <CalibrationSteps sessionId={sessionId} matrixDone onFinish={() => navigate(exitHref)} />
      </Shell>
    );
  }

  // ── Phase 1: the scope-aware matrix (shared step indicator on top) ─────────
  const matrixStepItems: StepItem[] = [
    { label: "Matriz", state: "active" },
    { label: "Perfis", state: "upcoming" },
    { label: "Validação", state: "upcoming" },
    { label: "Aprendizado", state: "upcoming" },
  ];
  const stepIndicator = <WorkflowStepper items={matrixStepItems} />;

  if (kind === "new_discipline") {
    return (
      <DisciplineMatrixStep
        modelId={modelId ?? ""}
        stepIndicator={stepIndicator}
        onCreated={({ disciplineId }) => startSession({ modelId: modelId ?? "", disciplineId })}
      />
    );
  }

  return (
    <ModelsNewPage
      stepIndicator={stepIndicator}
      onCreated={(model) => startSession({ modelId: model.id, disciplineId: null })}
    />
  );
}
